import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  VStack,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Box,
  AlertIcon,
  Alert,
  RadioGroup,
  Stack,
  Radio,
} from '@chakra-ui/react';

import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';

import { useDispatch, useSelector } from 'react-redux';
import { learningPlanActions } from 'store/modules/learningPlan/learningPlan-slice';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import MultiEmailInput from 'components/common/forms/MultiEmailInput';
import { ReducerStatus } from 'utils/constants';
import { useEffect, useState } from 'react';
import {
  getLearningPlanAction,
  updateLearningPlanAction,
} from 'store/modules/learningPlan/learningPlan-action';
import ResultModal from 'components/common/modals/ResultModal';
import useToastNotif from 'hooks/useToastNotif';
import { TOAST_ERROR } from 'utils/constants/toast';

function UpdateLearningPlanMd() {
  const dispatch = useDispatch();
  const { generateToastNotif } = useToastNotif();
  // State for managing selected users/groups in MultiEmailInput
  const [selectedEmails, setSelectedEmails] = useState([]);

  /**
   * Handles adding a new item (user or group) to the selected emails list
   */
  const handleEmailAdd = (item) => {
    setSelectedEmails((prev) => [...prev, item]);
  };

  /**
   * Handles removing an item from the selected emails list
   */
  const handleEmailRemove = (item) => {
    setSelectedEmails((prev) =>
      prev.filter(
        (email) => email.value !== item.value || email.type !== item.type
      )
    );
  };

  const learningPlan = useSelector((state) => state.learningPlan.learningPlan);

  const getLearningPlan = useSelector(
    (state) => state.learningPlan.getLearningPlan
  );

  const updateLearningPlan = useSelector(
    (state) => state.learningPlan.updateLearningPlan
  );

  const [formInputs, setFormInputs] = useState({
    name: '',
    description: '',
  });

  const [isPublished, setIsPublished] = useState('false');

  const formValidation = Yup.object({
    name: Yup.string().max(155).required('Required'),
    description: Yup.string().max(1000).required('Required'),
  });

  const handlePublishStatusChange = (e) => {
    setIsPublished(e);
  };

  const handleSubmitForm = (formikObject) => {
    // Convert selectedEmails to simple email strings for backend compatibility
    const emails = selectedEmails.map((item) => item.value);

    const learningPlanData = {
      ...formikObject,
      OwnerGroups: emails,
      isPublished: Boolean(isPublished),
    };

    dispatch(
      updateLearningPlanAction({ learningPlanData, id: updateLearningPlan.id })
    );
  };

  const handleResultButton = () => {
    dispatch(
      learningPlanActions.setUpdateLearningPlan({
        status: ReducerStatus.IDLE,
      })
    );
    if (updateLearningPlan.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        name: '',
        description: '',
      });
      setSelectedEmails([]);

      dispatch(
        learningPlanActions.setUpdateLearningPlan({
          modal: false,
          id: null,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
    }
  };

  const handleToggleUpdateModal = (isOpen) => {
    dispatch(
      learningPlanActions.setUpdateLearningPlan({
        modal: isOpen,
      })
    );
  };

  useEffect(() => {
    if (updateLearningPlan.modal && updateLearningPlan.id) {
      dispatch(getLearningPlanAction(updateLearningPlan.id));
    }
  }, [dispatch, updateLearningPlan.id, updateLearningPlan.modal]);

  useEffect(() => {
    if (learningPlan && updateLearningPlan.modal) {
      setFormInputs({
        name: learningPlan.name,
        description: learningPlan.description,
      });

      const parsedEmails = JSON.parse(learningPlan?.ownerGroups);
      if (
        Array.isArray(parsedEmails) &&
        parsedEmails.every((group) => typeof group === 'string')
      ) {
        // Convert existing emails format to MultiEmailInput format
        const convertedEmails = parsedEmails.map((email) => ({
          label: email,
          value: email,
          type: 'groups',
          data: { name: email, mail: email, email },
        }));
        setSelectedEmails(convertedEmails);
      } else {
        setSelectedEmails([]);
      }

      setIsPublished(learningPlan?.isPublished.toString());
    }
  }, [learningPlan, updateLearningPlan.modal]);

  useEffect(() => {
    if (getLearningPlan.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getLearningPlan.error?.data?.status || '404',
        getLearningPlan.error?.data?.title || 'Not Found',
        true,
        false
      );

      dispatch(
        learningPlanActions.setUpdateLearningPlan({
          modal: false,
          id: null,
        })
      );
    }
  }, [getLearningPlan, generateToastNotif, dispatch]);

  return (
    <Box>
      {updateLearningPlan.status === ReducerStatus.SUCCEEDED ||
      updateLearningPlan.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateLearningPlan.modal}
          onSubmit={handleResultButton}
          isError={updateLearningPlan.status === ReducerStatus.FAILED}
          title={
            updateLearningPlan.status === ReducerStatus.SUCCEEDED
              ? 'Learning Plan Updated'
              : null
          }
          message={
            updateLearningPlan.status === ReducerStatus.SUCCEEDED
              ? 'The learning plan has been updated successfully.'
              : updateLearningPlan.error?.data?.title || 'Server Error'
          }
          errorObject={updateLearningPlan.error?.data?.errors}
        />
      ) : (
        <Modal
          size="2xl"
          isOpen={updateLearningPlan.modal}
          onClose={() => handleToggleUpdateModal(false)}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Update Learning Plan</ModalHeader>
            <ModalCloseButton />

            <Formik
              enableReinitialize
              initialValues={formInputs}
              onSubmit={(values) => {
                handleSubmitForm(values);
              }}
              validationSchema={formValidation}
            >
              {() => (
                <Form>
                  <ModalBody my={3}>
                    {getLearningPlan.status === ReducerStatus.LOADING ? (
                      <Alert my={4} status="loading" variant="left-accent">
                        <AlertIcon />
                        Fetching data please wait
                      </Alert>
                    ) : (
                      <VStack spacing={6} align="stretch">
                        <Field name="name">
                          {({ field, form }) => (
                            <FormikFormControl
                              label="Name"
                              helperText="Enter the name of the learning plan. Make it concise and descriptive."
                              errors={form.errors.name}
                              form={form}
                              touched={form.touched.name}
                            >
                              <Input {...field} />
                            </FormikFormControl>
                          )}
                        </Field>

                        <Field name="description">
                          {({ field, form }) => (
                            <FormikFormControl
                              label="Description"
                              helperText="Provide a detailed description of the learning plan. Mention the key objectives and what learners can expect to gain."
                              errors={form.errors.description}
                              touched={form.touched.description}
                            >
                              <Textarea {...field} />
                            </FormikFormControl>
                          )}
                        </Field>

                        <MultiEmailInput
                          label="Owner Groups"
                          helperText="Search and select users or distribution groups that have the authority to modify and use this learning plan. You can switch between searching users and groups using the dropdown."
                          selectedItems={selectedEmails}
                          isError={false}
                          errorMessage=""
                          onItemAdd={handleEmailAdd}
                          onItemRemove={handleEmailRemove}
                          defaultSearchType="groups"
                        />

                        <FormikFormControl
                          label="Published Status"
                          helperText="Choose between 'Draft' if the learning plan is still in progress and not ready for classes access, or 'Published' if the learning plan is ready for classes."
                        >
                          <RadioGroup
                            value={isPublished}
                            onChange={handlePublishStatusChange}
                          >
                            <Stack spacing={5} direction="row">
                              <Radio colorScheme="purple" value="false">
                                Draft
                              </Radio>
                              <Radio colorScheme="green" value="true">
                                Published
                              </Radio>
                            </Stack>
                          </RadioGroup>
                        </FormikFormControl>
                      </VStack>
                    )}
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      mr={3}
                      onClick={() => handleToggleUpdateModal(false)}
                    >
                      Close
                    </Button>
                    <Button
                      colorScheme="red"
                      px={10}
                      type="submit"
                      isLoading={
                        updateLearningPlan.status === ReducerStatus.LOADING
                      }
                    >
                      Submit
                    </Button>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

export default UpdateLearningPlanMd;
